import { Box, Button, FormControl, FormHelperText, Stack } from "@mui/material";
import { FormikErrors, FormikProps, getIn } from "formik";
import { chain, isEmpty } from "lodash-es";
import { useMemo, useState } from "react";

import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import AppBadge from "@/common/components/Badge/AppBadge";
import AppButton from "@/common/components/Button/AppButton";
import AppIcon from "@/common/components/Icons/AppIcon";
import GeneralPriceSummaryDisplay from "@/common/components/PriceSummary/GeneralPriceSummaryDisplay";
import { DamageCostEvaluationFormPermissions } from "@/common/helpers/entity/damageCostEvaluation";
import { noop } from "@/common/helpers/function";
import { IdHelper } from "@/common/helpers/id";
import { TypeHelper } from "@/common/helpers/type";
import { VehicleDamageDataInputDtoLocal } from "@/common/mapping/mappers/vehicleDamage";
import { cast } from "@/common/ts/conversions";
import { EntityInputFormInfo } from "@/common/ts/entity";
import { ValidationHelper } from "@/common/validation";
import { VehicleDamageDto } from "@/core/api/generated/v1";

import { GenericDamageViewerMode } from "../../VehicleVisualModel/Viewer/v2/GenericDamage/VisualModelEditorOfGenericDamage";
import {
  DamageCostEvaluationFormSettings,
  DamageCostEvaluationItemDamageValues,
  DamageCostEvaluationItemValues,
  DamageCostEvaluationValues,
  createNewEmptyItem,
} from "../DamageCostEvaluationCreateUpdate";
import { DamageCostEvaluationDamageEditor } from "./DamageCostEvaluationDamageEditor";
import { DamageCostEvaluationDamageEditorModal } from "./DamageCostEvaluationDamageEditorModal";
import DamageCostEvaluationItemInput from "./DamageCostEvaluationItemInput";

interface Props {
  formInfo: EntityInputFormInfo;
  formPerms: DamageCostEvaluationFormPermissions;
  formSettings: DamageCostEvaluationFormSettings;
  formikProps: FormikProps<DamageCostEvaluationValues>;
  defaultVehicleDamages: Nil<VehicleDamageDto[]>;
}

export function DamageCostEvaluationItemsInput({
  formInfo,
  formPerms,
  formSettings,
  formikProps,
  defaultVehicleDamages,
}: Props) {
  const { values, errors, setFieldValue } = formikProps;

  const [isDamageEditorModalOpen, setIsDamageEditorModalOpen] = useState(false);
  const [damageDataToUpdate, setDamageDataToUpdate] =
    useState<Nil<VehicleDamageDataInputDtoLocal>>(undefined);

  const selectedDamageIds = useMemo(
    () =>
      values.items?.flatMap((x) => x.damages?.map((y) => y.damage?.id || "").filter(Boolean) ?? []),
    [values.items],
  );

  const isGroupByPartTypeEnabled = useMemo(() => {
    const items = formikProps.values.items;

    const hasAtLeastTwoItems = (items?.length || 0) >= 2;

    const isAllDamagesInItemHaveSamePartType = items
      ?.filter((item) => Boolean(item))
      ?.every((item) => {
        const partTypeIds = item.damages?.map((d) => d.damage?.partTypeId).filter(Boolean);
        return (partTypeIds?.length || 0) > 0 && partTypeIds?.every((id) => id === partTypeIds[0]);
      });

    const firstPartTypes =
      items?.map((item) => item.damages?.at(0)?.damage?.partTypeId || "").filter(Boolean) ?? [];
    const counts = new Map<string, number>();
    for (const id of firstPartTypes) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    const isAtLeastTwoItemsShareSamePartType = Array.from(counts.values()).some(
      (count) => count >= 2,
    );

    return (
      hasAtLeastTwoItems && isAllDamagesInItemHaveSamePartType && isAtLeastTwoItemsShareSamePartType
    );
  }, [formikProps.values.items]);

  const damagesData = useMemo(
    () =>
      values.items?.flatMap((x) => x.damages?.map((y) => y.damage!) || []).filter(Boolean) || [],
    [values.items],
  );
  const damageDataByIdMap = useMemo(
    () =>
      chain(damagesData?.filter((x) => x && x.id) || [])
        .keyBy((x) => x.id || "")
        .mapValues((x) => x)
        .value(),
    [damagesData],
  );

  const isAnyItems = useMemo(() => TypeHelper.isNotEmptyArray(values.items), [values.items]);
  // const isAnyDamages = useMemo(() => TypeHelper.isNotEmptyArray(damagesData), [damagesData]);

  // NB: error array indexes must match the original data array indexes, so use placeholders
  const errorPlaceholder = undefined;
  const damagesDataErrors = useMemo<
    Array<FormikErrors<VehicleDamageDataInputDtoLocal> | typeof errorPlaceholder>
  >(() => {
    const damagesDataFlat =
      values.items?.flatMap(
        (item, itemIndex) =>
          item.damages?.map((itemDamage, itemDamageIndex) => ({
            itemIndex,
            itemDamageIndex,
            damageData: itemDamage.damage,
          })) || [],
      ) || [];
    return damagesDataFlat
      .map((x) => getIn(errors, `items[${x.itemIndex}].damages[${x.itemDamageIndex}].damage`))
      .map((damageDataErrors: Nil<FormikErrors<VehicleDamageDataInputDtoLocal>>) =>
        damageDataErrors
          ? cast<FormikErrors<VehicleDamageDataInputDtoLocal>>({
              ...damageDataErrors,
              partTypeId: damageDataErrors?.partTypeId,
              damageTypeId: damageDataErrors?.damageTypeId,
              area: damageDataErrors?.area,
              projection: damageDataErrors?.projection,
              point: damageDataErrors?.point,
              notes: damageDataErrors?.notes,
              attachments: damageDataErrors?.attachments,
            })
          : errorPlaceholder,
      );
  }, [values.items, errors]);

  return (
    <Box>
      <Stack gap={2}>
        {!isAnyItems && <NoDataAlert title='No items yet' />}

        <Stack gap={1}>
          {/* Controls */}
          {isAnyItems && (
            <Stack
              direction={{ xxs: "column", md: "row" }}
              sx={{ gap: 1, alignItems: "flex-start" }}
            >
              {/* Edit damages in editor */}
              <AppButton
                disabled={!formPerms.canEdit}
                variant='outlined'
                size='small'
                color='text'
                startIcon={<AppIcon of='edit' />}
                onClick={() => {
                  setIsDamageEditorModalOpen(true);
                }}
              >
                Edit local damages
              </AppButton>

              {/* Group by part type */}
              <AppBadge
                badgeContent={
                  <AppIcon
                    color='text'
                    of='info'
                    sx={{ width: "18px", height: "18px" }}
                    fontSize='small'
                    tooltipTitle='Grouping by part type is available when at least two items have the same part type and all damages within each item share the same part type.'
                  />
                }
                sx={{
                  "& .MuiBadge-badge": {
                    p: 0.2,
                    height: "unset",
                    borderRadius: "50%",
                    backgroundColor: (t) => t.palette.background.paper,
                  },
                }}
              >
                <AppButton
                  variant='outlined'
                  size='small'
                  color='text'
                  disabled={!isGroupByPartTypeEnabled || !formPerms.canEdit}
                  onClick={() => {
                    const groupedItems: Record<string, typeof values.items> = {};
                    (values.items || []).forEach((item) => {
                      const partTypeId = item.damages?.[0]?.damage?.partTypeId;
                      if (!partTypeId) return;
                      if (!groupedItems[partTypeId]) {
                        groupedItems[partTypeId] = [];
                      }
                      groupedItems[partTypeId].push(item);
                    });

                    const newItems = Object.entries(groupedItems).map(([_partTypeId, items]) => {
                      const mergedDamages = items?.flatMap(
                        (item) =>
                          item.damages?.map((d) =>
                            cast<DamageCostEvaluationItemDamageValues>({
                              ...d,
                              // damageId: d.damage?.id,
                              damage: d.damage,
                              damageLocal: d.damageLocal,
                            }),
                          ) || [],
                      );
                      const mergedAttachments = items?.map((item) => item.attachments || []).flat();
                      const mergedNotes = items
                        ?.flatMap((item) => item.notes || undefined)
                        .filter(Boolean)
                        .join("\n");

                      return cast<DamageCostEvaluationItemValues>({
                        tempId: IdHelper.newId(),
                        name: items?.[0].name || "",
                        damages: mergedDamages,
                        repairSpec: items?.[0].repairSpec,
                        initialAttachments: mergedAttachments,
                        attachments: mergedAttachments,
                        notes: mergedNotes,
                      });
                    });

                    setFieldValue("items", newItems);
                  }}
                >
                  Group by part type
                </AppButton>
              </AppBadge>
            </Stack>
          )}

          {/* Damage editor (view-only) */}
          {isAnyItems && values.vehicleId && (
            <DamageCostEvaluationDamageEditor
              mode={GenericDamageViewerMode.View}
              formPerms={formPerms}
              vehicleId={values.vehicleId}
              initialDamagesData={damagesData}
              damagesData={damagesData}
              errors={damagesDataErrors}
              onCancel={noop}
              onSave={noop}
            />
          )}
        </Stack>

        {/* Items */}
        <Stack direction='column' spacing={1}>
          {values.vehicleId &&
            values.vehicle &&
            values.items?.map((item, i) => (
              <Box
                key={item.id || item.tempId || i}
                sx={{
                  border: (t) => `1px solid ${t.palette.divider}`,
                  borderRadius: (t) => t.shapeCustom.borderRadiusCard,
                  p: 2,
                }}
              >
                <DamageCostEvaluationItemInput
                  formInfo={formInfo}
                  formPerms={formPerms}
                  formSettings={formSettings}
                  formikProps={formikProps}
                  vehicleId={values.vehicleId || ""}
                  vehicle={values.vehicle}
                  currency={values.currency}
                  selectedDamageIds={selectedDamageIds}
                  itemCount={values?.items?.length ?? 0}
                  item={item}
                  itemIndex={i}
                  isPrefillRepairSpec={!isEmpty(defaultVehicleDamages)}
                  onUpdateInlineDamageData={(params) => {
                    setDamageDataToUpdate(params.damageData);
                    setIsDamageEditorModalOpen(true);
                  }}
                />
              </Box>
            ))}
        </Stack>

        {errors?.items && (
          <FormControl margin='dense' fullWidth error>
            <FormHelperText error>Some items have errors. Please review them.</FormHelperText>

            <FormHelperText error>
              {ValidationHelper.getFormikErrorsAsString(errors.items, {
                isIncludeNested: false,
              })}
            </FormHelperText>
          </FormControl>
        )}

        <Stack
          direction={{ xxs: "column", md: "row" }}
          spacing={2}
          sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
        >
          {/* Add item */}
          <Button
            disabled={!formPerms.canAddItems}
            variant='outlined'
            size='small'
            color='text'
            startIcon={<AppIcon of='add' />}
            onClick={() => setFieldValue("items", [...(values.items || []), createNewEmptyItem()])}
          >
            Add item
          </Button>

          {/* Summary */}
          {isAnyItems && (
            <Box>
              <Stack direction='row' justifyContent='flex-end'>
                <GeneralPriceSummaryDisplay
                  sx={{ minWidth: { xxs: "100%", md: "300px" } }}
                  summary={{
                    currency: values.currency,
                    subTotal: values.subTotal || 0,
                    subTotalIncDiscount: values.subTotalIncDiscount,
                    discount: values.discount,
                    tax: values.tax,
                    total: values.total || 0,
                  }}
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </Stack>

      {/* Damage editor modal */}
      {values.vehicleId && (
        <DamageCostEvaluationDamageEditorModal
          open={isDamageEditorModalOpen}
          onClose={() => {
            setIsDamageEditorModalOpen(false);
            setDamageDataToUpdate(undefined);
          }}
          editorProps={{
            mode: GenericDamageViewerMode.Edit,
            formPerms: formPerms,
            vehicleId: values.vehicleId,
            initialDamagesData: damagesData,
            initialDamageDataToUpdate: damageDataToUpdate,
            errors: damagesDataErrors,
            onCancel: () => {
              setIsDamageEditorModalOpen(false);
              setDamageDataToUpdate(undefined);
            },
            onSave: (newValues) => {
              // apply changes to DCE input structure:
              // - created damage: add new item
              // - updated damage: replace in existing item
              // - deleted damage: remove from existing item and remove item if no more damages
              // regarding temp FE id:
              // - new local data might have temp id so we need to check old id.
              // - locally updated data might have temp id so we need to check old id.
              // NB: don't use originalId here as it has different use case and won't work for this purpose (e.g. damage corrections).

              const newIdsMap = chain(newValues.filter((x) => x && x.id))
                .keyBy((x) => x.id || "")
                .mapValues((x) => x)
                .value();
              const newIdsBeforeUpdateLocalMap = chain(
                newValues.filter((x) => x && x.idBeforeUpdateLocal),
              )
                .keyBy((x) => x.idBeforeUpdateLocal || "")
                .mapValues((x) => x)
                .value();

              const created = newValues.filter(
                (x) =>
                  x.id &&
                  !(x.id in damageDataByIdMap) &&
                  (!x.idBeforeUpdateLocal || !(x.idBeforeUpdateLocal in damageDataByIdMap)),
              );
              const updated = newValues.filter(
                (x) =>
                  x.id &&
                  (x.id in damageDataByIdMap ||
                    (x.idBeforeUpdateLocal && x.idBeforeUpdateLocal in damageDataByIdMap)),
              );
              const deleted = damagesData.filter(
                (x) => x.id && !(x.id in newIdsMap) && !(x.id in newIdsBeforeUpdateLocalMap),
              );

              let newItems = [...(values.items || [])];

              // created
              for (const data of created) {
                const itemToAdd = createNewEmptyItem();
                itemToAdd.damages = [{ damage: data, damageLocal: undefined }];
                itemToAdd.notes = data.notes;
                itemToAdd.attachments = data.attachments;
                newItems.push(itemToAdd);
              }

              // updated
              for (const data of updated) {
                newItems = newItems.map((item) => ({
                  ...item,
                  damages: item.damages?.map((itemDamage) =>
                    itemDamage.damage?.id === data.id ||
                    itemDamage.damage?.id === data.idBeforeUpdateLocal
                      ? { ...itemDamage, damage: data, damageLocal: undefined }
                      : itemDamage,
                  ),
                }));

                // add new attachments from damages data to the item, but preserve explicitly uploaded attachments
                newItems.forEach((item) => {
                  const damageDataAttachments =
                    item.damages?.flatMap((x) => x.damage?.attachments || []) || [];
                  damageDataAttachments.forEach((attachmentCandidate) => {
                    const shouldAdd =
                      !item.attachments ||
                      !item.attachments.some((x) => x.file?.id == attachmentCandidate.file?.id);
                    if (shouldAdd) {
                      item.attachments?.push(attachmentCandidate);
                    }
                  });
                });
              }

              // deleted
              for (const data of deleted) {
                newItems = newItems
                  .map((item) => ({
                    ...item,
                    damages: item.damages?.filter(
                      (itemDamage) => itemDamage.damage?.id && itemDamage.damage.id != data.id,
                    ),
                  }))
                  .filter((item) => TypeHelper.isNotEmptyArray(item.damages));
              }

              // ensure no stale data to future updates
              newItems.forEach((item) => {
                item.damages?.forEach((damageData) => {
                  if (damageData.damage) {
                    damageData.damage.idBeforeUpdateLocal = null;
                  }
                });
              });

              console.log("DamageCostEvaluationItemsInput.onSave", {
                newValues: newValues,
                damageDataByIdMap,
                newIdsMap,
                newIdsBeforeUpdateLocalMap,
                created,
                updated,
                deleted,
                items: values.items,
                newItems,
              });

              setFieldValue("items", newItems);
              setIsDamageEditorModalOpen(false);
              setDamageDataToUpdate(undefined);
            },
          }}
        />
      )}
    </Box>
  );
}
